// Anonymous counts (see worker/event.ts): what kind of thing happened and
// which site a search went to. Never the search words, nothing per person.
import type {
  CountedEvent,
  ErrorReport,
  How,
  Kind,
  Where,
} from '../../worker/event';
import { CUSTOM_PREFIX } from './custom';
import type { Interpretation } from './resolve';

/** Which site and how a search was written, for counting. */
export function describe(
  interp: Interpretation
): Pick<CountedEvent, 's' | 'h'> {
  if (interp.kind === 'external') return { s: 'ddg', h: 'ddg' };
  if (interp.kind !== 'engine') return { s: '', h: '' };
  const s = interp.engine.id.startsWith(CUSTOM_PREFIX)
    ? 'custom'
    : interp.engine.id;
  const h: How = interp.usedDefault
    ? 'default'
    : interp.preset
      ? 'preset'
      : Object.keys(interp.values).length
        ? 'filters'
        : 'plain';
  return { s, h };
}

function optedOut(): boolean {
  const nav = navigator as Navigator & { globalPrivacyControl?: boolean };
  return nav.globalPrivacyControl === true || nav.doNotTrack === '1';
}

/** Sends one note. Silent, and skipped on local copies and opt-outs. */
function send(body: object) {
  try {
    if (location.protocol !== 'https:' || optedOut()) return;
    // A beacon is still sent when the page navigates away straight after.
    navigator.sendBeacon('/e', JSON.stringify(body));
  } catch {
    // Counting must never get in the way of a search.
  }
}

/** Counts one event. */
export function track(
  k: Kind,
  detail: Partial<Pick<CountedEvent, 's' | 'h'>> = {}
) {
  send({ k, ...detail });
}

/**
 * The first place in the site's own code that a stack trace mentions: the
 * chunk's name (without its hash) and line:column. Undefined when the error
 * didn't come from our code (a browser extension, say).
 */
export function frameOf(
  stack: string | undefined
): { f: string; at: string } | undefined {
  const m = /\/assets\/([\w-]+?)-[\w-]{8}\.js:(\d+):(\d+)/.exec(stack ?? '');
  if (!m?.[1]) return undefined;
  return { f: m[1].toLowerCase().slice(0, 32), at: `${m[2]}:${m[3]}` };
}

let reported = 0;

/**
 * Reports an error by where it happened, its type and its place in our code.
 * Never the message or the page address: both can hold search words.
 */
export function reportError(
  w: Where,
  error: unknown,
  { ownCodeOnly = false } = {}
) {
  // A handful per page is plenty; a loop shouldn't flood the counter.
  if (reported >= 5) return;
  const report = describeError(w, error, ownCodeOnly);
  if (!report) return;
  reported++;
  send(report);
}

/** What an error report carries; undefined when it shouldn't be sent. */
export function describeError(
  w: Where,
  error: unknown,
  ownCodeOnly = false
): ErrorReport | undefined {
  const stack = error instanceof Error ? error.stack : undefined;
  const frame = frameOf(stack);
  if (ownCodeOnly && !frame) return undefined;
  const name = error instanceof Error ? error.name : '';
  return {
    k: 'error',
    w,
    t: /^[A-Za-z]{1,32}$/.test(name) ? name : 'Error',
    f: frame?.f ?? '',
    at: frame?.at ?? '',
    v: __BUILD_VERSION__,
  };
}

const SEEN = 'abs:seen';

/** Counts this browser's first visit, once. */
export function trackFirst() {
  try {
    if (localStorage.getItem(SEEN)) return;
    localStorage.setItem(SEEN, '1');
    track('first');
  } catch {
    // No storage, no count.
  }
}
